/**
 * DEĞİŞİKLİKTE OTOMATİK ŞİFRELİ YEDEK İNDİRME — sağlayıcı (gelen karar
 * 03.11.2026; çekirdek `lib/otoIndir.ts`).
 *
 * `YedekSaglayici`nın içinde durur: bulut depolama seçili ve BAĞLIYKEN hiçbir
 * şey yapmaz (yedek zaten gidiyor). Değilse ve anahtar açıksa:
 *   - kayıtlar ya da sanal hesaplar değişince, seçilen bekleme süresinden
 *     sonra kasanın şifreli dosyası iner (art arda değişiklikler tek dosyada),
 *   - sekme arka plana geçerken bekleyen indirme bir kez denenir,
 *   - sekme kapanırken tarayıcının "ayrılmak istiyor musunuz" penceresi çıkar
 *     (uygulamanın bilerek yaptığı yeniden yüklemelerde çıkmaz),
 *   - bu tarayıcıdaki ilk otomatik indirmeden sonra "indi mi?" diye sorulur.
 * Yalnızca ayarlar değiştiyse ya da otomatik indirme çalışmıyorsa bant
 * (`YedekBandi`) sorar; bant tek dokunuşla aynı dosyayı indirir.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { downloadBlob } from "../lib/files";
import { elleYedekOzeti } from "../lib/elleYedek";
import {
  beklemeSn,
  indirmeFarki,
  indirmeOzetleri,
  izinOku,
  izinYaz,
  otoDosyaAdi,
  otoIndirAcikMi,
  sonIndirmeyiOku,
} from "../lib/otoIndir";
import type { IndirmeFarki, IndirmeIzni, SonIndirme } from "../lib/otoIndir";
import { bilerekAyriliyorMu } from "../lib/sayfaCikisi";
import { ELLE_YEDEK_AYARI, OTO_INDIR_AYARI, OTO_INDIR_BEKLEME_AYARI, SON_INDIRME_AYARI } from "../lib/yedekAyarlari";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useYedek } from "./YedekSaglayici";

export interface OtoIndirBaglami {
  /** Bulut depolama seçili ve bağlı: anahtar kapalı ve açılamaz. */
  readonly bulutBagli: boolean;
  /** Kasadaki anahtar (varsayılan açık). `null` = henüz okunmadı. */
  readonly acik: boolean | null;
  readonly beklemeSn: number;
  /** Son indirmeden beri ne değişti; `null` = henüz bakılmadı. */
  readonly fark: IndirmeFarki | null;
  readonly son: SonIndirme | null;
  /** Bu tarayıcıda "indi mi?" sorusunun cevabı. */
  readonly izin: IndirmeIzni;
  /** İlk otomatik indirmeden sonra "indi mi?" sorusu açık. */
  readonly soru: boolean;
  /** Açılışta indirilmemiş kritik değişiklik vardı (önceki indirme olmadı). */
  readonly acilistaBekleyen: boolean;
  /** Otomatik indirme şu an çalışır durumda. */
  readonly etkin: boolean;
  readonly hata: unknown;
  /** Kullanıcının dokunuşuyla: şifreli dosyayı şimdi indir. */
  indir(): Promise<void>;
  acikYap(acik: boolean): Promise<void>;
  beklemeYap(sn: number): Promise<void>;
  izinCevapla(indi: boolean): void;
  izniSifirla(): void;
}

const BaglamO = createContext<OtoIndirBaglami | null>(null);

/** Kilitli kasa yoksa (sahte depo, eski kip) `null`: özellik yok. */
export function useOtoIndir(): OtoIndirBaglami | null {
  return useContext(BaglamO);
}

interface Okunan {
  readonly acik: boolean;
  readonly bekleme: number;
  readonly son: SonIndirme | null;
  readonly fark: IndirmeFarki;
}

export interface OtoIndirSaglayiciProps {
  readonly children: ReactNode;
  /** Testler indirmeyi yakalar; üretimde tarayıcının indirmesi. */
  readonly indirici?: (blob: Blob, ad: string) => void;
  readonly saat?: () => Date;
  /** Testler bekleme süresini kısaltır (saniye başına ms). */
  readonly birSaniyeMs?: number;
  /** Testler kilitsiz depoyla şifreli dosya üretir; üretimde Worker (`repo.sifreliYedek`). */
  readonly olustur?: () => Promise<string>;
}

export function OtoIndirSaglayici({
  children,
  indirici = downloadBlob,
  saat = () => new Date(),
  birSaniyeMs = 1000,
  olustur,
}: OtoIndirSaglayiciProps) {
  const { repo, revision, invalidate } = useRepositoryContext();
  const yedek = useYedek();
  const sifreli = useMemo(() => (olustur !== undefined ? { olustur } : repo.sifreliYedek), [olustur, repo]);
  const durum = yedek?.durum ?? null;
  const bulutBilinir = durum !== null && durum.kurulu !== null;
  const bulutBagli = durum !== null && durum.kurulu === true && !durum.baglantiGerekli && durum.hata === null;

  const [okunan, setOkunan] = useState<Okunan | null>(null);
  const [izin, setIzin] = useState<IndirmeIzni>(izinOku);
  const [soru, setSoru] = useState(false);
  const [acilistaBekleyen, setAcilistaBekleyen] = useState<boolean | null>(null);
  const [hata, setHata] = useState<unknown>(undefined);

  useEffect(() => {
    if (sifreli === undefined) return;
    let iptal = false;
    void (async () => {
      const o = await indirmeOzetleri(await repo.exportPlainJson());
      const [a, b, s] = await Promise.all([
        repo.settings.get(OTO_INDIR_AYARI),
        repo.settings.get(OTO_INDIR_BEKLEME_AYARI),
        repo.settings.get(SON_INDIRME_AYARI),
      ]);
      const son = sonIndirmeyiOku(s);
      const fark = indirmeFarki(o, son);
      if (iptal) return;
      setOkunan({ acik: otoIndirAcikMi(a), bekleme: beklemeSn(b), son, fark });
      setAcilistaBekleyen((onceki) => (onceki === null ? fark === "veri" : onceki));
    })().catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [repo, revision, sifreli]);

  const etkin = sifreli !== undefined && bulutBilinir && !bulutBagli && okunan?.acik === true && izin !== "hayir";

  const calisiyor = useRef(false);
  const indir = useCallback(
    async (kendiliginden: boolean): Promise<void> => {
      if (sifreli === undefined || calisiyor.current) return;
      calisiyor.current = true;
      try {
        const json = await repo.exportPlainJson();
        const o = await indirmeOzetleri(json);
        const metin = await sifreli.olustur();
        const an = saat();
        const dosya = otoDosyaAdi(an);
        indirici(new Blob([metin], { type: "application/octet-stream" }), dosya);
        // Tarayıcı indirmenin inip inmediğini söylemez: başlatılan indirme
        // yapılmış sayılır (Ayarlar'da sorumluluk açıkça yazılı).
        const kayit: SonIndirme = { veri: o.veri, ayar: o.ayar, zaman: an.toISOString(), dosya, tur: "oto" };
        await repo.settings.set(SON_INDIRME_AYARI, JSON.stringify(kayit));
        // Kalıcı depolama uyarısı için elle yedek sayılır (gelen karar 03.11.2026).
        await repo.settings.set(ELLE_YEDEK_AYARI, await elleYedekOzeti(json));
        setHata(undefined);
        setAcilistaBekleyen(false);
        if (kendiliginden && izinOku() === null) setSoru(true);
        invalidate();
      } catch (e: unknown) {
        setHata(e);
      } finally {
        calisiyor.current = false;
      }
    },
    // `saat` ve `indirici` testte sabit, üretimde modül işlevi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repo, sifreli, invalidate],
  );

  // Değişiklikten sonra bekleme; yeni değişiklik sayacı baştan kurar.
  useEffect(() => {
    if (!etkin || okunan === null || okunan.fark !== "veri") return;
    const t = setTimeout(() => void indir(true), okunan.bekleme * birSaniyeMs);
    return () => clearTimeout(t);
  }, [etkin, okunan, indir, birSaniyeMs]);

  // Sekme arka plana geçerken bekleyen indirme BİR KEZ denenir (aynı kasa
  // hâli için ısrar edilmez); kapanırken tarayıcının penceresi.
  const son = useRef({ etkin, okunan, bulutBagli, revision });
  son.current = { etkin, okunan, bulutBagli, revision };
  const denenen = useRef<number | null>(null);
  useEffect(() => {
    if (sifreli === undefined) return;
    const doc = globalThis.document;
    const pencere = globalThis.window;
    if (doc === undefined || pencere === undefined) return;
    const gizlendi = (): void => {
      const s = son.current;
      if (doc.visibilityState !== "hidden" || !s.etkin || s.okunan?.fark !== "veri") return;
      if (denenen.current === s.revision) return;
      denenen.current = s.revision;
      void indir(true);
    };
    const ayriliyor = (e: BeforeUnloadEvent): void => {
      const s = son.current;
      if (bilerekAyriliyorMu() || s.bulutBagli || s.okunan?.acik !== true || s.okunan.fark !== "veri") return;
      e.preventDefault();
      e.returnValue = "";
    };
    doc.addEventListener("visibilitychange", gizlendi);
    pencere.addEventListener("beforeunload", ayriliyor);
    return () => {
      doc.removeEventListener("visibilitychange", gizlendi);
      pencere.removeEventListener("beforeunload", ayriliyor);
    };
  }, [sifreli, indir]);

  const deger = useMemo<OtoIndirBaglami>(
    () => ({
      bulutBagli,
      acik: okunan?.acik ?? null,
      beklemeSn: okunan?.bekleme ?? beklemeSn(null),
      fark: okunan?.fark ?? null,
      son: okunan?.son ?? null,
      izin,
      soru,
      acilistaBekleyen: acilistaBekleyen === true,
      etkin,
      hata,
      indir: () => indir(false),
      async acikYap(acik) {
        await repo.settings.set(OTO_INDIR_AYARI, acik ? "acik" : "kapali");
        invalidate();
      },
      async beklemeYap(sn) {
        await repo.settings.set(OTO_INDIR_BEKLEME_AYARI, String(sn));
        invalidate();
      },
      izinCevapla(indi) {
        const v = indi ? "evet" : "hayir";
        izinYaz(v);
        setIzin(v);
        setSoru(false);
      },
      izniSifirla() {
        izinYaz(null);
        setIzin(null);
      },
    }),
    [bulutBagli, okunan, izin, soru, acilistaBekleyen, etkin, hata, indir, repo, invalidate],
  );

  if (sifreli === undefined) return <>{children}</>;
  return <BaglamO.Provider value={deger}>{children}</BaglamO.Provider>;
}
